// 기프트 이미지 업로드 전 자동 축소 — 웹 등록(giftContent.js)·수정(giftManage.js)·모바일(Status.js) 공통.
// 휴대폰으로 찍은 사진(3~8MB)도 그대로 올릴 수 있게, 긴 변 1,500px로 줄여서 올린다.
// 서버(SharpPipe)가 원본과 800px 썸네일을 따로 저장하므로 1,500px이면 화질 손실이 없다.

export const GIFT_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
const MAX_EDGE = 1500;
const JPEG_QUALITIES = [0.85, 0.7, 0.55];

const loadImage = async file => {
  if (typeof window.createImageBitmap === 'function') {
    try {
      // 휴대폰 사진의 EXIF 회전값을 픽셀에 반영해 옆으로 눕지 않게 한다
      return await window.createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // 아래 <img> 방식으로 다시 시도
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = err => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise(resolve => canvas.toBlob(resolve, type, quality));

const baseName = name => (name || 'image').replace(/\.[^.]+$/, '');

/**
 * 업로드할 기프트 이미지를 준비한다.
 * - 긴 변 1,500px 이하이고 3MB 이하면 원본 그대로
 * - 아니면 긴 변 1,500px로 줄인다 (JPG는 JPG, PNG는 투명 배경 유지를 위해 먼저 PNG로 시도)
 * - GIF는 줄이면 움직임이 사라지므로 줄이지 않는다
 * 줄여도 3MB를 넘거나 이미지를 읽을 수 없으면 null — 호출하는 쪽에서 imageSizeError 안내.
 */
export const prepareGiftImage = async file => {
  if (!file) return null;
  const isGif = file.type === 'image/gif' || /\.gif$/i.test(file.name);
  if (isGif) return file.size <= GIFT_IMAGE_MAX_BYTES ? file : null;

  let source;
  try {
    source = await loadImage(file);
  } catch {
    return file.size <= GIFT_IMAGE_MAX_BYTES ? file : null;
  }

  try {
    const { width, height } = source;
    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
    if (scale === 1 && file.size <= GIFT_IMAGE_MAX_BYTES) return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';

    const isPng = file.type === 'image/png' || /\.png$/i.test(file.name);
    if (isPng) {
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      const pngBlob = await canvasToBlob(canvas, 'image/png');
      if (pngBlob && pngBlob.size <= GIFT_IMAGE_MAX_BYTES) {
        return new File([pngBlob], `${baseName(file.name)}.png`, { type: 'image/png' });
      }
    }

    // JPG로 저장 — 투명 부분은 흰 배경으로 채운다
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    for (const quality of JPEG_QUALITIES) {
      const jpgBlob = await canvasToBlob(canvas, 'image/jpeg', quality);
      if (jpgBlob && jpgBlob.size <= GIFT_IMAGE_MAX_BYTES) {
        return new File([jpgBlob], `${baseName(file.name)}.jpg`, { type: 'image/jpeg' });
      }
    }
    return null;
  } finally {
    if (typeof source.close === 'function') source.close();
  }
};
